/**
 * Browser entry point.
 *
 * Finds the two elements `index.html` declares — `#game-canvas` (R6) and `#ui-root`
 * (R8) — builds the `Game`, boots it, and makes sure that every way this can fail ends
 * with a readable message instead of a black rectangle. Spine §8 asks for exactly that
 * on a machine without WebGL2, and the same path catches anything else that throws
 * during boot.
 */

import { Game, webgl2Supported } from './game';

const CANVAS_ID = 'game-canvas';
const UI_ROOT_ID = 'ui-root';

function fatal(title: string, detail: string): void {
  const host = document.getElementById(UI_ROOT_ID) ?? document.body;
  const box = document.createElement('div');
  box.className = 'fatal-screen';
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;' +
    'justify-content:center;gap:1rem;padding:2rem;text-align:center;pointer-events:auto;' +
    'background:#050A14;color:#CFE8FF;font-family:Silkscreen,monospace;z-index:9999';

  const h = document.createElement('h1');
  h.textContent = title;
  h.style.cssText = 'font-size:1.25rem;letter-spacing:0.08em;margin:0;color:#7FD4FF';

  const p = document.createElement('p');
  p.textContent = detail;
  p.style.cssText = 'font-size:0.8rem;line-height:1.8;max-width:46ch;margin:0;opacity:0.85';

  box.appendChild(h);
  box.appendChild(p);
  host.appendChild(box);

  const canvas = document.getElementById(CANVAS_ID);
  if (canvas) canvas.setAttribute('hidden', '');
}

function start(): void {
  const canvas = document.getElementById(CANVAS_ID);
  const uiRoot = document.getElementById(UI_ROOT_ID);

  if (!(canvas instanceof HTMLCanvasElement) || !(uiRoot instanceof HTMLElement)) {
    fatal(
      'LUMEN ABYSS could not start',
      `The page is missing #${CANVAS_ID} or #${UI_ROOT_ID}. Reload; if it persists, the build is incomplete.`,
    );
    return;
  }

  if (!webgl2Supported()) {
    fatal(
      'WebGL2 required',
      'This browser cannot open a WebGL2 context. Update the browser, or enable hardware acceleration in its settings, then reload.',
    );
    return;
  }

  let game: Game;
  try {
    game = new Game(canvas, uiRoot);
  } catch (err) {
    fatal('LUMEN ABYSS failed to start', String(err instanceof Error ? err.message : err));
    return;
  }

  game.boot().catch((err: unknown) => {
    fatal('LUMEN ABYSS failed to start', String(err instanceof Error ? err.message : err));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
